import axiosInstance from "./apiinterseptor";
import { getApiErrorMessage } from "../utils/apiError";

export type BlogStatus = 'draft' | 'published' | 'archived';

export interface BlogAuthor {
  _id: string;
  username: string;
  email: string;
  profilePhotoUrl?: string;
}

export interface Blog {
  _id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  featuredImage?: string;
  author: BlogAuthor | string;
  status: BlogStatus;
  tags: string[];
  views: number;
  likes: number;
  publishedAt?: string;
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateBlogRequest {
  title: string;
  content: string;
  excerpt?: string;
  featuredImage?: string;
  status?: BlogStatus;
  tags?: string[];
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];
}

export interface UpdateBlogRequest {
  title?: string;
  content?: string;
  excerpt?: string;
  featuredImage?: string;
  status?: BlogStatus;
  tags?: string[];
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];
}

export interface BlogListResponse {
  success: boolean;
  data: {
    blogs: Blog[];
    pagination: {
      page: number;
      limit: number;
      totalPages: number;
      totalResults: number;
    };
  };
}

export interface BlogResponse {
  success: boolean;
  data: {
    blog: Blog;
  };
  message?: string;
}

export interface BlogStatsResponse {
  success: boolean;
  data: {
    total: number;
    published: number;
    draft: number;
    archived: number;
    totalViews: number;
    totalLikes: number;
    byStatus: Array<{
      _id: BlogStatus;
      count: number;
      totalViews: number;
      totalLikes: number;
    }>;
  };
}

// Create a new blog (Admin only)
export const createBlog = async (blogData: CreateBlogRequest): Promise<BlogResponse> => {
  try {
    const response = await axiosInstance.post('/blogs', blogData);
    return response.data;
  } catch (error: unknown) {
    throw new Error(getApiErrorMessage(error, 'Failed to create blog'));
  }
};

// Get all blogs with filters
export const getBlogs = async (params: {
  page?: number;
  limit?: number;
  status?: string;
  authorId?: string;
  tag?: string;
  search?: string;
} = {}): Promise<BlogListResponse> => {
  try {
    const response = await axiosInstance.get('/blogs', {
      params: {
        page: params.page || 1,
        limit: params.limit || 10,
        status: params.status,
        authorId: params.authorId,
        tag: params.tag,
        search: params.search
      }
    });
    return response.data;
  } catch (error: unknown) {
    throw new Error(getApiErrorMessage(error, 'Failed to fetch blogs'));
  }
};

// Get blog by ID
export const getBlogById = async (blogId: string): Promise<BlogResponse> => {
  try {
    const response = await axiosInstance.get(`/blogs/${blogId}`);
    return response.data;
  } catch (error: unknown) {
    throw new Error(getApiErrorMessage(error, 'Failed to fetch blog'));
  }
};

// Get blog by slug
export const getBlogBySlug = async (slug: string): Promise<BlogResponse> => {
  try {
    const response = await axiosInstance.get(`/blogs/slug/${slug}`);
    return response.data;
  } catch (error: unknown) {
    throw new Error(getApiErrorMessage(error, 'Failed to fetch blog'));
  }
};

// Update blog (Admin/Author only)
export const updateBlog = async (blogId: string, blogData: UpdateBlogRequest): Promise<BlogResponse> => {
  try {
    const response = await axiosInstance.put(`/blogs/${blogId}`, blogData);
    return response.data;
  } catch (error: unknown) {
    throw new Error(getApiErrorMessage(error, 'Failed to update blog'));
  }
};

// Delete blog (Admin/Author only)
export const deleteBlog = async (blogId: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axiosInstance.delete(`/blogs/${blogId}`);
    return response.data;
  } catch (error: unknown) {
    throw new Error(getApiErrorMessage(error, 'Failed to delete blog'));
  }
};

// Like blog
export const likeBlog = async (blogId: string): Promise<{ success: boolean; data: { likes: number }; message: string }> => {
  try {
    const response = await axiosInstance.post(`/blogs/${blogId}/like`);
    return response.data;
  } catch (error: unknown) {
    throw new Error(getApiErrorMessage(error, 'Failed to like blog'));
  }
};

// Get blog statistics
export const getBlogStats = async (authorId?: string): Promise<BlogStatsResponse> => {
  try {
    const response = await axiosInstance.get('/blogs/stats', {
      params: authorId ? { authorId } : {}
    });
    return response.data;
  } catch (error: unknown) {
    throw new Error(getApiErrorMessage(error, 'Failed to fetch blog statistics'));
  }
};

// Upload blog image
export const uploadBlogImage = async (file: File, folderPath: string = 'blogs/images'): Promise<{ success: boolean; data: { url: string } }> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folderPath', folderPath);

    const response = await axiosInstance.post('/uploads/file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    
    if (response.data.status !== 'success' || !response.data.data?.fileUrl) {
      throw new Error(response.data.message || 'Upload failed. No image URL was returned.');
    }

    return {
      success: true,
      data: {
        url: response.data.data.fileUrl
      }
    };
  } catch (error: unknown) {
    throw new Error(getApiErrorMessage(error, 'Failed to upload image'));
  }
};
